import { format } from "date-fns";
import { sortBy } from "lodash";

import { type SalesPageMetricSpec } from "@north-beam/nb-client-side";
import {
  type IMCAttributionWindowDays,
  type INBAccountingMode,
} from "@north-beam/nb-common";

import { type DataExportMetric } from "./types";

export function createDataExportMetric(
  availableAttributionModels: string[],
  spec: SalesPageMetricSpec,
  attributionModels: Set<string>,
  attributionWindows: Set<IMCAttributionWindowDays>,
  accountingModes: Set<INBAccountingMode>,
): DataExportMetric {
  const allowedAttributionModels = new Set<string>();

  if (spec.modifiers.attributionModel === "all") {
    attributionModels.forEach((e) => allowedAttributionModels.add(e));

    if (allowedAttributionModels.size === 0) {
      allowedAttributionModels.add(availableAttributionModels[0]);
    }
  } else if (spec.modifiers.attributionModel !== "none") {
    allowedAttributionModels.add(spec.modifiers.attributionModel);
  }

  const allowedAttributionWindows = new Set<IMCAttributionWindowDays>();

  if (spec.modifiers.attributionWindows.length === 1) {
    allowedAttributionWindows.add(spec.modifiers.attributionWindows[0]);
  } else if (spec.modifiers.attributionWindows.length > 1) {
    spec.modifiers.attributionWindows
      .filter((e) => attributionWindows.has(e))
      .forEach((e) => allowedAttributionWindows.add(e));

    if (allowedAttributionWindows.size === 0) {
      allowedAttributionWindows.add(spec.modifiers.attributionWindows[0]);
    }
  }

  const allowedAccountingModes = new Set<INBAccountingMode>();

  if (spec.modifiers.accountingModes.length === 1) {
    allowedAccountingModes.add(spec.modifiers.accountingModes[0]);
  } else if (spec.modifiers.accountingModes.length > 1) {
    spec.modifiers.accountingModes
      .filter((e) => accountingModes.has(e))
      .forEach((e) => allowedAccountingModes.add(e));

    if (allowedAccountingModes.size === 0) {
      allowedAccountingModes.add(spec.modifiers.accountingModes[0]);
    }
  }

  return {
    id: spec.id,
    label: spec.name,
    attributionModels: allowedAttributionModels,
    attributionWindows: allowedAttributionWindows,
    accountingModes: allowedAccountingModes,
  };
}

export function createInitialMetrics(
  availableAttributionModels: string[],
  salesPageMetricSpecs: SalesPageMetricSpec[],
  selectedMetricIds: string[],
  initialAttributionModelIds: string[],
  initialAttributionWindowIds: IMCAttributionWindowDays[],
  initialAccountingModeIds: INBAccountingMode[],
) {
  const specsById = new Map(salesPageMetricSpecs.map((e) => [e.id, e]));
  const metrics: DataExportMetric[] = [];

  for (const metricId of selectedMetricIds) {
    const spec = specsById.get(metricId);

    if (spec) {
      metrics.push(
        createDataExportMetric(
          availableAttributionModels,
          spec,
          new Set(initialAttributionModelIds),
          new Set(initialAttributionWindowIds),
          new Set(initialAccountingModeIds),
        ),
      );
    }
  }

  if (metrics.length === 0) {
    metrics.push(
      createDataExportMetric(
        availableAttributionModels,
        sortBy(
          salesPageMetricSpecs.filter((e) => e.group === "default"),
          (e) => e.name.toLowerCase(),
        )[0],
        new Set(initialAttributionModelIds),
        new Set(initialAttributionWindowIds),
        new Set(initialAccountingModeIds),
      ),
    );
  }

  return metrics;
}

export function generateDefaultScheduledExportName() {
  const date = format(new Date(), "yyyy_MM_dd_HH_mm_ss");
  return `Scheduled_export_${date}`;
}
